/**
 * CrmInboxFilter - Filtro de inbox para dashboard de conversas
 * 
 * Permite selecionar uma inbox ativa ou "Todas Inboxes".
 * Segue o padrão do CrmWorkspaceFilter (Popover + Command).
 * Suporta tamanho compacto via className (h-[22px]).
 * 
 * @author Sistema
 * @version 1.0.0
 * RELEASE: 2026-10-05 v1.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 * RELEASE: 2026-02-26 v1.0.0 - Criação inicial do filtro de inbox
 */

import React, { useState } from 'react';
import { Check, ChevronDown, Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Props do componente CrmInboxFilter
 * @param value - ID da inbox selecionada (null = todas)
 * @param onChange - Callback para mudança de seleção
 * @param className - Classes CSS adicionais (suporta h-[22px] para versão compacta)
 */
interface CrmInboxFilterProps {
  value: string | null;
  onChange: (inboxId: string | null) => void;
  className?: string;
  /**
   * Lista opcional de IDs permitidos (usada quando há um grupo de inbox
   * selecionado). Quando informado:
   *  - `null`/`undefined` → comportamento padrão (todas as inboxes).
   *  - `[]` → nenhuma inbox listada (grupo sem inboxes vinculadas).
   *  - `[ids...]` → filtra para exibir apenas as inboxes presentes na lista.
   */
  allowedIds?: string[] | null;
}

/** Tipo para inbox no filtro */
interface InboxOption {
  id: string;
  name: string;
  channel_type: string;
}

/**
 * Componente de filtro de inbox
 * Busca inboxes ativas do tenant e permite seleção via Popover + Command
 */
export function CrmInboxFilter({
  value,
  onChange,
  className,
  allowedIds,
}: CrmInboxFilterProps) {
  const { currentTenant } = useAuth();
  const [open, setOpen] = useState(false);

  // Buscar inboxes ativas do tenant
  const { data: allInboxes = [], isLoading } = useQuery({
    queryKey: ['crm-inboxes-filter', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      // v1.1.0: Migrado para RPC server-side (get_tenant_inboxes_safe).
      // Elimina SELECT direto na tabela `inboxes` e centraliza acesso atrás
      // da camada v2.0 de validação de tenant + log de bypass.
      const { data, error } = await supabase.rpc('get_tenant_inboxes_safe', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;

      // Filtra apenas inboxes ativas e ordena por nome no client
      // (volume típico de dezenas — overhead desprezível).
      return ((data || []) as any[])
        .filter((ib) => ib.status === 'active')
        .map((ib) => ({
          id: ib.id,
          name: ib.name,
          channel_type: ib.channel_type,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)) as InboxOption[];
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // Cache de 5 minutos
  });

  // Restringe a lista quando há um grupo selecionado (allowedIds informado).
  // Array vazio = grupo sem inboxes vinculadas → não exibe nenhuma opção.
  const inboxes = Array.isArray(allowedIds)
    ? allInboxes.filter((ib) => allowedIds.includes(ib.id))
    : allInboxes;

  // Inbox selecionada
  const selectedInbox = inboxes.find(ib => ib.id === value);

  // Handler para seleção
  const handleSelect = (inboxId: string | null) => {
    onChange(inboxId);
    setOpen(false);
  };

  // Detecta se é versão compacta baseado na classe
  const isCompact = className?.includes('h-[22px]');

  if (isLoading) {
    return <Skeleton className={cn(isCompact ? 'h-[22px] w-[100px]' : 'h-9 w-[160px]')} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 px-3 gap-2 font-normal border-0 bg-background rounded-full',
            className
          )}
        >
          {selectedInbox ? (
            <>
              <Inbox className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4')} />
              <span className={cn('truncate hidden sm:inline', isCompact ? 'max-w-[60px]' : 'max-w-[100px]')}>
                {selectedInbox.name}
              </span>
            </>
          ) : (
            <>
              <Inbox className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
              <span className="hidden sm:inline truncate">
                {isCompact ? 'Todas' : 'Todas Inboxes'}
              </span>
            </>
          )}
          <ChevronDown className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'opacity-50')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar inbox..." />
          <CommandList>
            <CommandEmpty>
              {Array.isArray(allowedIds) && allowedIds.length === 0
                ? 'Nenhuma inbox no grupo selecionado.'
                : 'Nenhuma inbox encontrada.'}
            </CommandEmpty>
            <CommandGroup>
              {/* Opção "Todas Inboxes" */}
              <CommandItem
                value="__all__"
                onSelect={() => handleSelect(null)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                    value === null
                      ? "border-primary bg-primary"
                      : "border-muted"
                  )}
                >
                  {value === null && (
                    <Check className="h-2.5 w-2.5 text-primary-foreground" />
                  )}
                </div>
                <Inbox className="h-4 w-4 mr-2 opacity-50" />
                <span>Todas Inboxes</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Inboxes">
              {inboxes.map(ib => (
                <CommandItem
                  key={ib.id}
                  value={ib.name}
                  onSelect={() => handleSelect(ib.id)}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                      value === ib.id
                        ? "border-primary bg-primary"
                        : "border-muted"
                    )}
                  >
                    {value === ib.id && (
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    )}
                  </div>
                  <Inbox className="h-4 w-4 mr-2 opacity-50" />
                  <span className="truncate">{ib.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
